import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SearchGlyph } from "../userui";
import {
  applyRoleTemplate, bulkPrivileges, editableItems, errorText, fetchCatalog, fetchMatrix, labelOf, saveRoleTemplate,
} from "./privilegeModel";
import { DEPT_FILTER_ALL, DEPT_FILTER_NONE } from "../userDepartments";
import "../user.css";

/* Users > Privileges (owner 11-03), in the user module's look (user.css .bmus / .bmpm): users x areas with a
   department filter (the 11-03 department routes), a "holds" filter (e.g. who holds User administration), bulk
   add / remove previewed with dry_run before anything is written, and role templates (edit + apply, previewed).
   Every write is a v2 add / remove list: retired and unknown ids are never touched, service accounts are not
   selectable, and bulk never removes User administration (one user at a time in Edit user). */

const tone = (held, total) => (!total || !held ? "none" : held === total ? "full" : "some");

const PreviewTable = ({ result, catalog }) => (
  <table className="bmpm-prev" data-testid="bulk-preview">
    <thead>
      <tr><th>User</th><th>Adds</th><th>Removes</th><th /></tr>
    </thead>
    <tbody>
      {(result?.results || []).map((r) => (
        <tr key={r.id}>
          <td>{r.username || `#${r.id}`}</td>
          <td className="add">{(r.added || []).map((id) => labelOf(catalog, id)).join(", ")}</td>
          <td className="rem">{(r.removed || []).map((id) => labelOf(catalog, id)).join(", ")}</td>
          <td className="note">{r.skipped ? `Skipped: ${r.why}` : r.changed ? "" : "No change"}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const PickList = ({ label, items, value, onChange, testid }) => {
  const [q, setQ] = useState("");
  const shown = items.filter((p) => !q || `${p.label} ${p.description}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <fieldset className="bmpm-pick" data-testid={testid}>
      <legend>{label} <span>{value.size} picked</span></legend>
      <input type="search" aria-label={`${label}: search`} placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="bmpm-pick-list">
        {shown.map((p) => (
          <label key={p.id}>
            <input type="checkbox" aria-label={`${label} ${p.label}`} checked={value.has(String(p.id))}
                   onChange={(e) => {
                     const n = new Set(value);
                     if (e.target.checked) n.add(String(p.id)); else n.delete(String(p.id));
                     onChange(n);
                   }} />
            <span>{p.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
};

const PrivilegeMatrix = ({ deptOptions = [] }) => {
  const [catalog, setCatalog] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [dept, setDept] = useState("");
  const [role, setRole] = useState("");
  const [holds, setHolds] = useState("");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [addIds, setAddIds] = useState(new Set());
  const [removeIds, setRemoveIds] = useState(new Set());
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("bulk");
  const [tplRole, setTplRole] = useState("");
  const [tplSel, setTplSel] = useState(new Set());
  const [tplPreview, setTplPreview] = useState(null);
  const [tplApplyMode, setTplApplyMode] = useState("add");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [cat, mx] = await Promise.all([fetchCatalog(), fetchMatrix(dept ? { department: dept } : {})]);
      setCatalog(cat);
      setMatrix(mx);
    } catch (err) {
      setError(errorText(err, "The privileges view could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [dept]);
  useEffect(() => { load(); }, [load]);

  const areas = matrix?.areas || [];
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (matrix?.data || []).filter((u) => {
      if (role && String(u.grole) !== String(role)) return false;
      if (holds && !(u.permissions || []).map(String).includes(String(holds))) return false;
      if (needle && !`${u.username} ${u.firstname || ""} ${u.lastname || ""}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [matrix, role, holds, q]);
  const editable = useMemo(() => editableItems(catalog), [catalog]);
  const pickable = rows.filter((u) => !u.service_account);
  const allPicked = pickable.length > 0 && pickable.every((u) => picked.has(u.id));
  const togglePick = (id) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const runBulk = async (dryRun) => {
    setBusy(true);
    setMessage("");
    try {
      const res = await bulkPrivileges({ userIds: [...picked], add: [...addIds], remove: [...removeIds], dryRun });
      setPreview(dryRun ? res : null);
      if (!dryRun) {
        setMessage(`Saved: ${res?.summary?.changed ?? 0} user(s) changed, ${res?.summary?.skipped ?? 0} skipped.`);
        setAddIds(new Set()); setRemoveIds(new Set()); setPicked(new Set());
        load();
      }
    } catch (err) {
      setMessage(errorText(err, "Nothing was saved."));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const roleRow = (catalog?.roles || []).find((r) => String(r.id) === String(tplRole));
  useEffect(() => {
    setTplSel(new Set(((roleRow && roleRow.template_visible) || []).map(String)));
    setTplPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tplRole, catalog]);

  const runTemplateSave = async (dryRun) => {
    setBusy(true); setMessage("");
    try {
      const res = await saveRoleTemplate(tplRole, [...tplSel], dryRun);
      if (dryRun) setTplPreview({ kind: "save", res });
      else { setTplPreview(null); setMessage(`Template saved for ${roleRow?.role}.`); load(); }
    } catch (err) { setMessage(errorText(err, "The template was not saved.")); }
    finally { setBusy(false); }
  };
  const runTemplateApply = async (dryRun) => {
    setBusy(true); setMessage("");
    try {
      const res = await applyRoleTemplate(tplRole, { userIds: [...picked], mode: tplApplyMode, dryRun });
      if (dryRun) setTplPreview({ kind: "apply", res });
      else {
        setTplPreview(null);
        setMessage(`Template applied: ${res?.summary?.changed ?? 0} user(s) changed, ${res?.summary?.skipped ?? 0} skipped.`);
        load();
      }
    } catch (err) { setMessage(errorText(err, "The template was not applied.")); }
    finally { setBusy(false); }
  };

  if (error) return <p className="bmpm-error" data-testid="matrix-error">{error}</p>;
  const gained = (tplPreview?.res?.changes || []).filter((c) => c.added_role);
  const lost = (tplPreview?.res?.changes || []).filter((c) => !c.added_role);

  return (
    <div className="bmpm" data-testid="privilege-matrix">
      <div className="bmus-bar-sub bmpm-filters">
        <label className="bmus-search bmpm-search">
          {SearchGlyph}
          <input type="text" aria-label="Matrix search" placeholder="Search name or user ID" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="bmus-pick">
          <span className="bmus-pick-k">Department</span>
          <select className="bmus-select" aria-label="Matrix department" value={dept}
                  onChange={(e) => { setDept(e.target.value); setPicked(new Set()); }}>
            <option value="">All departments</option>
            {deptOptions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            <option value={DEPT_FILTER_ALL}>All-departments users</option>
            <option value={DEPT_FILTER_NONE}>Department not set</option>
          </select>
        </div>
        <div className="bmus-pick">
          <span className="bmus-pick-k">Role</span>
          <select className="bmus-select" aria-label="Matrix role" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="">All roles</option>
            {(matrix?.roles || []).map((r) => <option key={r.id} value={r.id}>{r.role}</option>)}
          </select>
        </div>
        <div className="bmus-pick">
          <span className="bmus-pick-k">Holds</span>
          <select className="bmus-select" aria-label="Matrix holds" value={holds} onChange={(e) => setHolds(e.target.value)}>
            <option value="">Any privilege</option>
            {(catalog?.privileges || []).filter((p) => p.active).map((p) => (
              <option key={p.id} value={p.id}>{p.label}{p.editable ? "" : " (retired)"} · {p.holders}</option>
            ))}
          </select>
        </div>
        <span className="bmpm-count">{loading ? "Loading…" : `${rows.length} users`}</span>
      </div>

      <div className="bmus-card">
        <div className="bmus-scroll">
          <table className="bmus-table bmpm-table" data-testid="matrix-table">
            <thead>
              <tr>
                <th><input type="checkbox" aria-label="Select all users" checked={allPicked}
                           onChange={(e) => setPicked(e.target.checked ? new Set(pickable.map((u) => u.id)) : new Set())} /></th>
                <th>Name</th>
                <th>Role</th>
                <th>Location</th>
                {areas.map((a) => <th key={a.key} title={a.description} className="c">{a.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td className="bmus-empty" colSpan={4 + areas.length}>No users match this view.</td></tr>
              ) : rows.map((u) => (
                <tr key={u.id} className={u.service_account ? "ro" : ""}>
                  <td><input type="checkbox" aria-label={`Select ${u.username}`} disabled={u.service_account}
                             checked={picked.has(u.id)} onChange={() => togglePick(u.id)} /></td>
                  <td>
                    <span className="bmus-name">
                      <b>{`${u.firstname || ""} ${u.lastname || ""}`.trim() || u.username}</b>
                      <span>{u.username}{u.service_account ? " · service account" : ""}{u.is_admin ? " · user admin" : ""}</span>
                    </span>
                  </td>
                  <td><span className="bmus-when">{u.rolename}</span></td>
                  <td><span className="bmus-when">{u.department?.label}</span></td>
                  {areas.map((a) => {
                    const c = u.by_area?.[a.key] || { held: 0, total: 0, ids: [] };
                    if (!c.total) return <td key={a.key} className="c">–</td>;
                    return (
                      <td key={a.key} className="c">
                        <span className={`bmpm-cell ${tone(c.held, c.total)}`}
                              title={(c.ids || []).map((id) => labelOf(catalog, id)).join(", ") || "none"}>
                          {c.held}/{c.total}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bmus-tabs bmpm-modes" role="group" aria-label="Privilege actions">
        <button type="button" className={`bmus-tab${mode === "bulk" ? " on" : ""}`} aria-pressed={mode === "bulk"} onClick={() => setMode("bulk")}>
          Bulk add / remove <span className="bmus-tab-n">{picked.size}</span>
        </button>
        <button type="button" className={`bmus-tab${mode === "templates" ? " on" : ""}`} aria-pressed={mode === "templates"} onClick={() => setMode("templates")}>
          Role templates
        </button>
      </div>

      {mode === "bulk" && (
        <div className="bmpm-panel" data-testid="bulk-bar">
          <p className="bmpm-hint">{picked.size} user(s) selected - tick them in the table. Retired privileges cannot be changed; User administration is never removed in bulk.</p>
          <div className="bmpm-picks">
            <PickList label="Add" items={editable} value={addIds} onChange={setAddIds} testid="bulk-add" />
            <PickList label="Remove" items={editable} value={removeIds} onChange={setRemoveIds} testid="bulk-remove" />
          </div>
          <div className="bmpm-acts">
            <button type="button" className="bmuf-cancel" disabled={busy || !picked.size || (!addIds.size && !removeIds.size)} onClick={() => runBulk(true)}>
              Preview
            </button>
            {preview && (
              <button type="button" className="bmuf-save" disabled={busy || !(preview?.summary?.would_change > 0)} onClick={() => runBulk(false)}>
                Apply to {preview?.summary?.would_change ?? 0} user(s)
              </button>
            )}
          </div>
          {preview && <PreviewTable result={preview} catalog={catalog} />}
        </div>
      )}

      {mode === "templates" && (
        <div className="bmpm-panel" data-testid="templates-bar">
          <div className="bmus-pick">
            <span className="bmus-pick-k">Role</span>
            <select className="bmus-select" aria-label="Template role" value={tplRole} onChange={(e) => setTplRole(e.target.value)}>
              <option value="">Select a role</option>
              {(catalog?.roles || []).map((r) => <option key={r.id} value={r.id}>{r.role}</option>)}
            </select>
          </div>
          {roleRow && (
            <>
              <PickList label={`${roleRow.role} template`} items={editable} value={tplSel}
                        onChange={(s) => { setTplSel(s); setTplPreview(null); }} testid="template-pick" />
              <div className="bmpm-acts">
                <button type="button" className="bmuf-cancel" disabled={busy} onClick={() => runTemplateSave(true)}>Preview template change</button>
                <select className="bmus-select" aria-label="Apply mode" value={tplApplyMode} onChange={(e) => setTplApplyMode(e.target.value)}>
                  <option value="add">Add missing privileges</option>
                  <option value="replace">Match the template exactly</option>
                </select>
                <button type="button" className="bmuf-cancel" disabled={busy} onClick={() => runTemplateApply(true)}>
                  Preview apply to {picked.size ? `${picked.size} selected user(s)` : `every active ${roleRow.role}`}
                </button>
              </div>
              <p className="bmpm-hint">A template is only a starting point: it reaches users through Apply, never by changing their Role.</p>
              {tplPreview?.kind === "save" && (
                <div className="bmpm-tplprev" data-testid="template-save-preview">
                  {gained.length === 0 && lost.length === 0 ? <p>No change to the template.</p> : (
                    <>
                      {gained.length > 0 && <p className="add">Template gains: {gained.map((c) => labelOf(catalog, c.id)).join(", ")}</p>}
                      {lost.length > 0 && <p className="rem">Template loses: {lost.map((c) => labelOf(catalog, c.id)).join(", ")}</p>}
                      <button type="button" className="bmuf-save" disabled={busy} onClick={() => runTemplateSave(false)}>Save template</button>
                    </>
                  )}
                </div>
              )}
              {tplPreview?.kind === "apply" && (
                <div className="bmpm-tplprev" data-testid="template-apply-preview">
                  <PreviewTable result={tplPreview.res} catalog={catalog} />
                  {(tplPreview.res?.summary?.would_change || 0) > 0 && (
                    <button type="button" className="bmuf-save" disabled={busy} onClick={() => runTemplateApply(false)}>
                      Apply to {tplPreview.res.summary.would_change} user(s)
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {message && <p className="bmpm-msg" role="status" data-testid="matrix-message">{message}</p>}
    </div>
  );
};

export default PrivilegeMatrix;
